"use client"

import { useMemo } from "react"

interface CoverArtProps {
    title: string
    slug: string
    className?: string
}

export function CoverArt({ title: _title, slug, className = "" }: CoverArtProps) {
    // Deterministic random using simple hashing
    const seed = useMemo(() => {
        let hash = 0
        for (let i = 0; i < slug.length; i++) {
            hash = slug.charCodeAt(i) + ((hash << 5) - hash)
        }
        return hash
    }, [slug])

    const gradients = [
        "from-blue-600 via-indigo-900 to-black",
        "from-emerald-600 via-teal-900 to-black",
        "from-violet-600 via-purple-900 to-black",
        "from-rose-600 via-pink-900 to-black",
        "from-orange-600 via-red-900 to-black",
        "from-cyan-600 via-blue-900 to-black",
    ]

    const selectedGradient = gradients[Math.abs(seed) % gradients.length]
    const patternType = Math.abs(seed) % 3 // 0: Grid, 1: Dots, 2: Noise

    return (
        <div className={`relative overflow-hidden bg-linear-to-br ${selectedGradient} ${className}`}>
            {/* Abstract Shapes */}
            <div className="absolute inset-0 opacity-30">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <pattern id={`grid-${seed}`} width="40" height="40" patternUnits="userSpaceOnUse">
                            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" opacity="0.2" />
                        </pattern>
                        <pattern id={`dots-${seed}`} width="20" height="20" patternUnits="userSpaceOnUse">
                            <circle cx="1" cy="1" r="1" fill="white" opacity="0.3" />
                        </pattern>
                    </defs>

                    {patternType === 0 && <rect width="100%" height="100%" fill={`url(#grid-${seed})`} />}
                    {patternType === 1 && <rect width="100%" height="100%" fill={`url(#dots-${seed})`} />}
                </svg>
            </div>

            {/* Optimized Dynamic Circles using Divs instead of SVG for better composition */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ contain: 'paint' }}>
                <div className="absolute top-[20%] left-[20%] w-[60%] h-[60%] bg-white/10 blur-[80px] rounded-full animate-pulse will-change-opacity" style={{ animationDuration: '8s' }} />
                <div className="absolute bottom-[20%] right-[20%] w-[80%] h-[80%] bg-white/5 blur-[100px] rounded-full animate-pulse will-change-opacity" style={{ animationDuration: '12s', animationDelay: '1s' }} />
            </div>

            {/* Noise Overlay */}
            <div
                className="absolute inset-0 z-10 opacity-[0.15] mix-blend-overlay"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                }}
            />

            {/* Title Overlay (Optional, if used as standalone card) */}
        </div>
    )
}
